"use client"

import React, { createContext, useContext, useState, useEffect } from "react"
import type { Product } from "./types"

interface WishlistContextType {
    wishlist: Product[]
    addToWishlist: (product: Product) => void
    removeFromWishlist: (productId: string) => void
    isInWishlist: (productId: string) => boolean
    clearWishlist: () => void
    wishlistCount: number
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined)

const WISHLIST_STORAGE_KEY = "stockwise_wishlist"

export function WishlistProvider({ children }: { children: React.ReactNode }) {
    const [wishlist, setWishlist] = useState<Product[]>([])
    const [isHydrated, setIsHydrated] = useState(false)

    // Charger la wishlist depuis le localStorage au montage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(WISHLIST_STORAGE_KEY)
            if (stored) {
                const parsed = JSON.parse(stored)
                setWishlist(parsed)
            }
        } catch (error) {
            console.error("Erreur lors du chargement de la wishlist:", error)
        } finally {
            setIsHydrated(true)
        }
    }, [])

    // Sauvegarder la wishlist dans le localStorage à chaque modification
    useEffect(() => {
        if (isHydrated) {
            try {
                localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlist))
            } catch (error) {
                console.error("Erreur lors de la sauvegarde de la wishlist:", error)
            }
        }
    }, [wishlist, isHydrated])

    const addToWishlist = (product: Product) => {
        setWishlist((prev) => {
            // Vérifier si le produit est déjà dans la wishlist
            if (prev.some((p) => p._id === product._id)) {
                return prev
            }
            return [...prev, product]
        })
    }

    const removeFromWishlist = (productId: string) => {
        setWishlist((prev) => prev.filter((p) => p._id !== productId))
    }

    const isInWishlist = (productId: string) => {
        return wishlist.some((p) => p._id === productId)
    }

    const clearWishlist = () => {
        setWishlist([])
    }

    const value = {
        wishlist,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        wishlistCount: wishlist.length,
    }

    return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
}

export function useWishlist() {
    const context = useContext(WishlistContext)
    if (context === undefined) {
        throw new Error("useWishlist must be used within a WishlistProvider")
    }
    return context
}
